/**
 * Pure helpers that rebuild a predicate when the user changes its
 * operator or switches the path to a different type family. Extracted
 * from FilterBuilderRow so the rebuild logic is unit-testable in
 * isolation -- in particular, so we can lock in the invariant that the
 * editor-only `_id` field survives both rebuilds (otherwise the row
 * loses its React key and remounts, leaking internal state like
 * `advancedOpen`).
 *
 * Both functions return a fresh predicate object suitable to hand to
 * the editor's `onChange` callback. They never mutate the input.
 */

/**
 * Rebuilds a predicate when the user picks a new operator within the
 * same type family. Drops value-shape-specific fields (haystack,
 * needles, value, lower, upper) since the next operator may use a
 * different shape; preserves `_id`, `path`, and the optional
 * `context` / `change` overrides.
 */
export function rebuildPredicateForOperator (predicate, nextType) {
  const { _id, path, context, change } = predicate
  const next = { _id, $type: nextType, path }
  if (context && context !== 'Self') next.context = context
  if (change) next.change = change
  return next
}

/**
 * Rebuilds a predicate when the user picks a path in a different type
 * family. Switches to the new family's first operator and seeds a clean
 * default value; preserves `_id` and the optional `context` / `change`
 * overrides. `predicateFamilies` is passed in (rather than imported)
 * so this stays pure and avoids a circular dependency with the
 * predicate registry.
 */
export function rebuildPredicateForFamily (predicate, nextFamily, canonicalPath, predicateFamilies) {
  const familyDef = predicateFamilies[nextFamily]
  if (!familyDef) return predicate
  const firstOp = familyDef.operators[0]
  const defaultValue = familyDef.defaultValue?.() || {}
  return {
    _id: predicate._id,
    $type: firstOp.$type,
    path: canonicalPath,
    ...defaultValue,
    ...(predicate.context && predicate.context !== 'Self' ? { context: predicate.context } : {}),
    ...(predicate.change ? { change: predicate.change } : {})
  }
}
